import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ACTIONS, URLS, STORAGE, APIS } from '../_config';
import { useUserApi } from '../store/api/hooks/userApiHook';
import { useStoreApi } from '../store/api/hooks/storeApiHook';
import { useDataSourceApi } from '../store/api/hooks/dataSourceApiHook';
import toast from 'react-hot-toast';
import Spinner from '../components/iconSvgs/spinner';
import axios from 'axios';

type SSOStatus = 'loading' | 'authenticating' | 'fetching-details' | 'success' | 'error';

const SSOLogin: React.FC = () => {
    const [status, setStatus] = useState<SSOStatus>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const hasInitiated = useRef(false);

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userAPI: any = useUserApi();
    const storeAPI: any = useStoreApi();
    const dataSourceAPI: any = useDataSourceApi();

    const getStatusMessage = () => {
        switch (status) {
            case 'loading':
                return 'Initializing SSO Login...';
            case 'authenticating':
                return 'Authenticating...';
            case 'fetching-details':
                return 'Loading user details...';
            case 'success':
                return 'Login successful! Redirecting...';
            case 'error':
                return errorMessage || 'An error occurred';
            default:
                return 'Processing...';
        }
    };

    const getUserDetailsAndSetup = useCallback(async (token: string) => {
        try {
            setStatus('fetching-details');
            
            const response = await axios.post(APIS.USER.USER_DETAILS, { token });
            
            if (response.data?.status) {
                const { store_id, store_name, branches, company, modules } = response.data;
                
                // Store the store information
                dispatch({
                    type: ACTIONS.STORE.GET_STORE,
                    payload: { store_id, name: store_name },
                });

                // Store the modules information
                dispatch({
                    type: ACTIONS.USER.SET_MODULES,
                    payload: modules || null,
                });

                // Store the company information
                dispatch({
                    type: ACTIONS.USER.SET_COMPANY,
                    payload: company || null,
                });

                // If branches exist, get data source for the first branch
                if (branches && branches.length > 0) {
                    const firstBranch = branches[0];
                    
                    const param: any = {
                        branch_id: firstBranch.branch_id,
                    };

                    dataSourceAPI.getDataSource(param, (msg: any, resp: any) => {
                        if (resp?.code === 200 && resp.data.length > 0) {
                            let inPending: any = null;
                            let failure: any = null;
                            const completed = resp?.data.find((item: any) => item.last_fetch_status === 'completed');
                            
                            if (!completed) {
                                inPending = resp?.data.find((item: any) => item.last_fetch_status === 'pending');
                                if (!inPending) {
                                    failure = resp?.data.find((item: any) => item.last_fetch_status !== 'completed' && item.last_fetch_status !== 'pending');
                                }
                            }

                            const rawPayload = completed || inPending || failure;
                            const branchPayload = {
                                ...rawPayload,
                                branch_id: rawPayload?.branch_id ?? firstBranch.branch_id,
                                branch_name: rawPayload?.branch_name ?? firstBranch.branch_name,
                                store_id: rawPayload?.store_id ?? store_id,
                            };

                            dispatch({
                                type: ACTIONS.STORE.GET_BRANCH,
                                payload: branchPayload,
                            });
                        } else {
                            // No data source, set basic branch info
                            const payload = {
                                source_type: null,
                                url: null,
                                last_fetch_date: null,
                                created_at: null,
                                ds_id: null,
                                store_id: store_id,
                                branch_id: firstBranch.branch_id,
                                branch_name: firstBranch.branch_name,
                                snapshot_id: null,
                                last_fetch_status: null,
                            };
                            
                            dispatch({
                                type: ACTIONS.STORE.GET_BRANCH,
                                payload: payload,
                            });
                        }

                        setStatus('success');
                        setTimeout(() => {
                            navigate(URLS.MODULE_SELECTION);
                        }, 500);
                    }, (error: any) => {
                        // Even on error, set basic branch info and navigate
                        const payload = {
                            source_type: null,
                            url: null,
                            last_fetch_date: null,
                            created_at: null,
                            ds_id: null,
                            store_id: store_id,
                            branch_id: firstBranch.branch_id,
                            branch_name: firstBranch.branch_name,
                            snapshot_id: null,
                            last_fetch_status: null,
                        };
                        
                        dispatch({
                            type: ACTIONS.STORE.GET_BRANCH,
                            payload: payload,
                        });

                        setStatus('success');
                        setTimeout(() => {
                            navigate(URLS.MODULE_SELECTION);
                        }, 500);
                    });
                } else {
                    // No branches, navigate to store creation
                    setStatus('error');
                    setErrorMessage('No branches found');
                    toast.error('No branches found for your account');
                    setTimeout(() => {
                        navigate(URLS.STORE.CREATE);
                    }, 2000);
                }
            } else {
                throw new Error('Failed to fetch user details');
            }
        } catch (error: any) {
            console.error('Error fetching user details:', error);
            setStatus('error');
            setErrorMessage(error?.message || 'Failed to load user details');
            toast.error(error?.message || 'Failed to load user details');
        }
    }, [dispatch, navigate, dataSourceAPI]);

    const handleSSOLogin = useCallback(async (encryptedSsoKey: string) => {
        try {
            // Call SSO login API with the already encrypted key
            setStatus('authenticating');
            const payload = { encrypted_sso_key: encryptedSsoKey };

            userAPI.ssoLogin(
                payload,
                (msg: any, resp: any) => {
                    if (resp?.code === 200 || resp?.data?.code === 200) {
                        toast.success('SSO Login successful');
                        
                        // Get token from response and fetch user details
                        const token = resp?.data?.access_token || resp?.access_token;
                        if (token) {
                            getUserDetailsAndSetup(token);
                        } else {
                            setStatus('error');
                            setErrorMessage('Token not received');
                            toast.error('Authentication token not received');
                        }
                    } else {
                        setStatus('error');
                        setErrorMessage(resp?.message || 'SSO Login failed');
                        toast.error(resp?.message || 'SSO Login failed');
                    }
                },
                (error: any, resp: any) => {
                    console.error('SSO Login error:', error, resp);
                    setStatus('error');
                    const message = resp?.detail?.message || resp?.message || error?.message || 'SSO Login failed';
                    setErrorMessage(message);
                    toast.error(message);
                }
            );
        } catch (error: any) {
            console.error('SSO login error:', error);
            setStatus('error');
            setErrorMessage(error?.message || 'Failed to process SSO login');
            toast.error(error?.message || 'Failed to process SSO login');
        }
    }, [userAPI, getUserDetailsAndSetup]);

    useEffect(() => {
        // Prevent double execution in React StrictMode
        if (hasInitiated.current) return;
        
        // Get the raw sso-key from URL
        // Note: The caller MUST URL-encode the encrypted key before putting it in the URL
        // e.g., encodeURIComponent(encryptedKey) to convert + to %2B, / to %2F, etc.
        // URLSearchParams.get() automatically decodes the value
        const ssoKey = searchParams.get('sso-key');
        
        if (!ssoKey) {
            setStatus('error');
            setErrorMessage('Missing SSO key parameter');
            toast.error('Invalid SSO login link');
            
            // Redirect to regular login after delay
            setTimeout(() => {
                navigate(URLS.USER.LOGIN);
            }, 3000);
            return;
        }

        hasInitiated.current = true;

        // ssoKey is already decoded by searchParams.get(), pass it directly
        handleSSOLogin(ssoKey);
    }, [searchParams, navigate, handleSSOLogin]);

    return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
            <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
                <div className="flex flex-col items-center">
                    {/* Logo */}
                    <img 
                        className="h-12 w-auto mb-8" 
                        src="/assets/images/Frame_2.png" 
                        alt="HubWallet logo" 
                    />

                    {/* Status indicator */}
                    <div className="flex flex-col items-center justify-center min-h-[200px]">
                        {status !== 'error' ? (
                            <>
                                <Spinner className="w-16 h-16 mb-6" />
                                <h2 className="text-xl font-semibold text-gray-800 mb-2">
                                    SSO Login
                                </h2>
                                <p className="text-gray-600 text-center">
                                    {getStatusMessage()}
                                </p>
                                
                                {/* Progress indicator */}
                                <div className="flex items-center gap-2 mt-6">
                                    <div className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                                        ['loading', 'authenticating', 'fetching-details', 'success'].includes(status) 
                                            ? 'bg-blue-500' 
                                            : 'bg-gray-300'
                                    }`} />
                                    <div className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                                        ['authenticating', 'fetching-details', 'success'].includes(status) 
                                            ? 'bg-blue-500' 
                                            : 'bg-gray-300'
                                    }`} />
                                    <div className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                                        ['fetching-details', 'success'].includes(status) 
                                            ? 'bg-blue-500' 
                                            : 'bg-gray-300'
                                    }`} />
                                    <div className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                                        status === 'success' 
                                            ? 'bg-green-500' 
                                            : 'bg-gray-300'
                                    }`} />
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Error state */}
                                <div className="w-16 h-16 mb-6 rounded-full bg-red-100 flex items-center justify-center">
                                    <svg 
                                        className="w-8 h-8 text-red-500" 
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round" 
                                            strokeWidth={2} 
                                            d="M6 18L18 6M6 6l12 12" 
                                        />
                                    </svg>
                                </div>
                                <h2 className="text-xl font-semibold text-gray-800 mb-2">
                                    SSO Login Failed
                                </h2>
                                <p className="text-red-600 text-center mb-6">
                                    {errorMessage}
                                </p>
                                <button
                                    onClick={() => navigate(URLS.USER.LOGIN)}
                                    className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                                >
                                    Go to Login
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SSOLogin;
